import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { get as _get } from 'lodash';
import {
  AdjusterInfo,
  ClaimDetailResponse,
  ClaimPlnarInfo,
  ClaimProgressEnum,
  GenericProductType,
  PaymentTransaction,
  PhotoInfo,
  PlnarAddress,
  ProviderInfo,
  ProviderInfoFromClaimDetails,
  ServiceTypeCode,
  StageNames,
  StageState,
} from '../../../types/type';
import {
  formatDate,
  formatTime,
  getAutoShopAddress,
  getClaimType,
  getProviderIDfromDisplayName,
  getStaticProgressStages,
  getUnformattedClaimNumber,
  getWebUrl,
  isDraftClaimNumber,
} from '../../../utils/Utilities';

interface ClaimSummary {
  policyNumber: string;
  reportedDate: string;
  timeOfIncident: string;
  incidentDescription: string;
  incidentType: string;
  claimOpenedDate: string;
  claimClosedDate: string;
  claimNumber: string;
  primaryAddressFirstLine: string;
  claimStatusCode: string;
  lossDate: string;
  claimDescription: string;
  lossCauseDescription: string;
  serviceProviderInfo: ProviderInfo[] | undefined;
  paymentInfo: PaymentTransaction[] | undefined;
  adjusterInfo: AdjusterInfo;
  claimProgressStates: StageState[];
  currentClaimProgressState: ClaimProgressEnum;
  lossLocationAddress: LossLocationAddress;
  autoClaimDetails: AutoClaimDetails[];
  cvpRefNumber?: string;
  insured: Insured;
  lineOfBusiness: GenericProductType;
  businessName?: string;
}

export interface Insured {
  firstName: string;
  lastName: string;
}

export interface AutoClaimDetails {
  year: string;
  make: string;
  model: string;
  vin: string;
}

export interface LossLocationAddress {
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}
export interface ClaimDetailState {
  claimSummary: ClaimSummary | undefined;
  claimPlnarInfo: ClaimPlnarInfo | undefined;
  claimPhotos: PhotoInfo[] | undefined;
}

const initialState: ClaimDetailState = {
  claimSummary: undefined,
  claimPlnarInfo: undefined,
  claimPhotos: undefined,
};

const claimDetailSlice = createSlice({
  name: 'claimDetail',
  initialState,
  reducers: {
    updateClaimDetailState(
      state,
      action: PayloadAction<{ claimDetail: ClaimDetailResponse; requestedClaimNumber?: string }>
    ) {
      const { claimDetail, requestedClaimNumber } = action.payload;
      const claimSummary = getClaimSummary(claimDetail);
      claimSummary.claimNumber = resolveClaimNumber(claimSummary.claimNumber, requestedClaimNumber);
      state.claimSummary = claimSummary;
      state.claimPlnarInfo = getClaimPlnarInfo(claimDetail);
    },
    updateClaimPhotosState(state, action: PayloadAction<{ claimPhotos: PhotoInfo[] }>) {
      const { claimPhotos } = action.payload;
      state.claimPhotos = claimPhotos;
    },
    clearClaimPhotosState(state) {
      state.claimPhotos = undefined;
    },
    updateClaimCvpRefNumState(state, action: PayloadAction<{ cvpRefNum: string }>) {
      const { cvpRefNum } = action.payload;
      if (state.claimSummary) {
        state.claimSummary.cvpRefNumber = cvpRefNum;
      }
    },
  },
});

/**
 * Right after a claim is submitted, claim-detail can still report the claim's temporary draft (TC) number
 * even though it was requested by the real claim number. The real number is the one every downstream
 * post-FNOL call (DRP, CVP, rentals, documents) needs, so it must not be replaced by the draft number.
 *
 * Whenever the response carries a draft number, the number we asked for wins:
 *   resolveClaimNumber('TC-01-021-12345', '01-021-12345') -> '0102112345'  // the bug: keep ours
 *   resolveClaimNumber('TC-009-432432', 'TC-009-432432')  -> 'TC009432432' // draft resume: same claim
 *   resolveClaimNumber('01010214593', '01010214593')      -> '01010214593' // normal: nothing to do
 *
 * Stored values are kept dash-free because the post-FNOL endpoints interpolate the claim number into
 * URLs without normalising it.
 *
 * `requestedClaimNumber` must be checked for a value first: it can arrive as null or '' (lodash `_get`
 * passes a null `claimsInfo.fullClaimNumber` through its default in app.tsx), and an empty string must
 * never replace a usable claim number.
 */
export function resolveClaimNumber(returnedClaimNumber: string, requestedClaimNumber?: string): string {
  if (requestedClaimNumber && isDraftClaimNumber(returnedClaimNumber)) {
    return getUnformattedClaimNumber(requestedClaimNumber);
  }
  return returnedClaimNumber;
}

function getClaimSummary(response: ClaimDetailResponse) {
  const claimInfo = _get(response, 'response[0].claimInfo', []);
  const incidentInfo = _get(response, 'response[1]');
  const addressInfo = _get(response, 'response[2]', []);
  const paymentInfo = _get(response, 'response[3]', []);
  const providerInfo = _get(response, 'response[4]', []);
  const adjusterContactID = _get(claimInfo, 'primaryFileHandler.contactID', '');
  const claimSummary: ClaimSummary = {
    policyNumber: _get(claimInfo, 'policyNumber', ''),
    reportedDate: formatDate(_get(claimInfo, 'reportedDate', '').split('T')[0]),
    lossDate: formatDate(_get(claimInfo, 'lossDate', '').split('T')[0]),
    timeOfIncident: formatTime(_get(claimInfo, 'lossDate', '').toString().slice(0, 19)),
    incidentDescription: _get(claimInfo, 'lossCauseDesc', ''),
    incidentType: _get(claimInfo, 'lossDate', ''),
    claimOpenedDate: _get(claimInfo, 'openDate', ''),
    claimClosedDate: _get(claimInfo, 'closeDate', ''),
    claimNumber: _get(claimInfo, 'claimNumber', ''),
    primaryAddressFirstLine: _get(claimInfo, 'lossLocationAddress.addressLine1', ''),
    claimStatusCode: getClaimStatusText(_get(claimInfo, 'claimStatusCode', '')),
    claimDescription: _get(claimInfo, 'claimDescription', ''),
    lossCauseDescription: _get(claimInfo, 'lossCauseDesc', ''),
    serviceProviderInfo: getProviderInfo(providerInfo),
    paymentInfo: getPaymentInfo(paymentInfo),
    adjusterInfo: getAdjusterInfo(adjusterContactID, addressInfo),
    claimProgressStates: getProgressStates(_get(claimInfo, 'claimProgress', '')),
    currentClaimProgressState: getCurrentProgressState(_get(claimInfo, 'claimProgress', '')),
    lossLocationAddress: getLossLocationAddress(_get(claimInfo, 'lossLocationAddress', '')),
    autoClaimDetails: mapAutoClaimDetail(incidentInfo),
    cvpRefNumber: mapCvpRefNumber(claimInfo, mapAutoClaimDetail(incidentInfo)),
    insured: mapInsured(addressInfo),
    lineOfBusiness: getClaimType(_get(claimInfo, 'policyType', '')),
    businessName: _get(claimInfo, 'businessName', ''),
  };
  return claimSummary;
}

function getClaimStatusText(statusText: string) {
  let status = '';
  switch (statusText) {
    case 'open':
      status = 'opened';
      break;
    case 'close':
      status = 'closed';
      break;
    default:
      status = statusText;
  }
  return status;
}
function getCurrentProgressState(progressState: string) {
  let currentProgressState: ClaimProgressEnum = ClaimProgressEnum.SUBMISSION;
  switch (progressState.toUpperCase()) {
    case ClaimProgressEnum.SUBMISSION:
    case ClaimProgressEnum.EVALUATION:
      currentProgressState = ClaimProgressEnum.EVALUATION;
      break;
    case ClaimProgressEnum.RESOLUTION:
      currentProgressState = ClaimProgressEnum.RESOLUTION;
      break;
    case ClaimProgressEnum.CLOSED:
      currentProgressState = ClaimProgressEnum.CLOSED;
      break;
  }
  return currentProgressState;
}

function getProgressStates(claimProgress: string) {
  const claimProgressStates: StageState[] = getStaticProgressStages();
  switch (claimProgress.toUpperCase()) {
    case ClaimProgressEnum.SUBMISSION:
    case ClaimProgressEnum.EVALUATION:
      claimProgressStates[0].completed = true;
      claimProgressStates[0].showProgress = true;
      claimProgressStates[1].viewActive = true;
      break;
    case ClaimProgressEnum.RESOLUTION:
      claimProgressStates[0].completed = true;
      claimProgressStates[0].showProgress = true;
      claimProgressStates[1].completed = true;
      claimProgressStates[1].showProgress = true;
      claimProgressStates[0].viewActive = false;
      claimProgressStates[1].viewActive = false;
      claimProgressStates[2].completed = false;
      claimProgressStates[2].viewActive = true;

      break;
    case ClaimProgressEnum.CLOSED:
      claimProgressStates[0].completed = true;
      claimProgressStates[0].showProgress = true;
      claimProgressStates[1].completed = true;
      claimProgressStates[1].showProgress = true;
      claimProgressStates[0].viewActive = false;
      claimProgressStates[1].viewActive = false;
      claimProgressStates[2].viewActive = false;
      claimProgressStates[2].completed = true;
      claimProgressStates[2].showProgress = true;
      claimProgressStates[3].completed = true;
      claimProgressStates[3].viewActive = true;
      break;
  }
  return claimProgressStates;
}

function getAdjusterInfo(contactID: string, addressInfo: any) {
  const adjusterInfoCollection = addressInfo.filter(
    (address: { contactID: string }) => address.contactID === contactID
  );
  const adjusterName = _get(adjusterInfoCollection[0], 'displayName', '');
  let adjusterEmail = '';
  const adjusterEmails = _get(adjusterInfoCollection[0], 'emailAddresses', []);
  if (adjusterEmails.length > 0) {
    adjusterEmail = adjusterEmails.filter((emailAddress: { primary: boolean }) => emailAddress.primary === true)[0]
      .emailAddress;
  }
  const adjusterPhoneNumbers = _get(adjusterInfoCollection[0], 'phoneNumbers', []);
  const adjusterPhoneNumberAreaCode = adjusterPhoneNumbers.length
    ? adjusterPhoneNumbers.filter((phoneNumber: { primary: boolean }) => phoneNumber.primary === true)[0].areaCode
    : '';
  const adjusterPhoneNumberWithOutAreaCode = adjusterPhoneNumbers.length
    ? adjusterPhoneNumbers.filter((phoneNumber: { primary: boolean }) => phoneNumber.primary === true)[0].number
    : '';
  const adjusterPhoneNumber = adjusterPhoneNumberAreaCode + adjusterPhoneNumberWithOutAreaCode;
  const adjusterInfo: AdjusterInfo = {
    adjusterName,
    adjusterEmail,
    adjusterPhoneNumber,
  };
  return adjusterInfo;
}

function getLossLocationAddress(address: LossLocationAddress) {
  const lossLocationAddress: LossLocationAddress = {
    addressLine1: address.addressLine1,
    city: address.city,
    state: address.state,
    postalCode: _get(address, 'postalCode', '').toString().slice(0, 5),
    country: address.country,
  };
  return lossLocationAddress;
}

function mapCvpRefNumber(claimInfo: any, autoClaimDetails: AutoClaimDetails[]) {
  const insuredVehicleVin = _get(autoClaimDetails[0], 'vin');
  const incidents = _get(claimInfo, 'incidents');
  const incident = incidents.find(
    (inc: { incidentType: string }) =>
      inc.incidentType === 'VehicleIncident' && _get(inc, 'vehicleIncident.Vehicle.VIN') === insuredVehicleVin
  );
  if (incident) {
    const fullRef = _get(incident, 'vehicleIncident.CVPRefNum', '').split('#');
    if (fullRef.length === 2) {
      return fullRef[1];
    } else {
      return fullRef[0];
    }
  }
  return '';
}

function mapInsured(contactInfo: any[]) {
  const contactToFind = contactInfo.find((contact) =>
    contact.claimRoles?.find((con: any) => con.roleCode === 'insured')
  );
  return {
    firstName: _get(contactToFind, 'firstName', ''),
    lastName: _get(contactToFind, 'lastName', ''),
  };
}

function mapAutoClaimDetail(incidentInfo: any) {
  return incidentInfo.flatMap((item: any) =>
    item?.vehicleSum
      ?.filter((vehicle: any) => vehicle.insuredLoss === 'insured')
      .map((vehicle: any) => {
        return {
          year: _get(vehicle, 'basicVehicleData.Year', ''),
          make: _get(vehicle, 'basicVehicleData.Make', ''),
          model: _get(vehicle, 'basicVehicleData.Model', ''),
          vin: _get(vehicle, 'basicVehicleData.VIN', ''),
        };
      })
  );
}

function getPaymentInfo(paymentInfoFromClaimDetails: PaymentTransaction[]) {
  const paymentInfoCollection: PaymentTransaction[] = [];
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
  // Filter payment info collection by roleCode. Show only roleCode which is === 'insured'
  const filteredPaymentInfoCollection = paymentInfoFromClaimDetails.filter(
    (claimDetail) =>
      _get(claimDetail, 'CostTypeCode') === 'claimcost' &&
      _get(claimDetail, 'LossPartyType') === 'insured' &&
      _get(claimDetail, 'TransactionStatusCode') === 'submitted' &&
      _get(claimDetail, 'TransactionTypeCode') === 'Payment' &&
      Number(_get(claimDetail, 'Amount')) > 0
  );

  filteredPaymentInfoCollection.forEach((aPaymentInfo) => {
    const unformattedAmount = _get(aPaymentInfo, 'Amount', 0);
    const paymentInfo: PaymentTransaction = {
      PayeeName: _get(aPaymentInfo, 'PayeeName', ''),
      Amount: `${formatter.format(parseFloat(unformattedAmount.toString()))}`,
      TransactionDate: formatDate(_get(aPaymentInfo, 'TransactionDate', '').split('T')[0]),
      Payees: [],
    };
    paymentInfoCollection.push(paymentInfo);
  });

  return paymentInfoCollection;
}

function getClaimPlnarInfo(response: ClaimDetailResponse) {
  const lossCause = _get(response, 'response[0].claimInfo.lossCauseDesc', '');
  const address: PlnarAddress = {
    address1: _get(response, 'response[0].claimInfo.lossLocationAddress.addressLine1', ''),
    address2: '',
    city: _get(response, 'response[0].claimInfo.lossLocationAddress.city', ''),
    stateProvince: _get(response, 'response[0].claimInfo.lossLocationAddress.state', ''),
    postalCode: _get(response, 'response[0].claimInfo.lossLocationAddress.postalCode', '').split('-')[0],
    country: _get(response, 'response[0].claimInfo.lossLocationAddress.country', ''),
  };
  const plnarInfo = {
    address: address,
    lossCause: lossCause,
  };
  return plnarInfo;
}

function getProviderInfo(providerInfoFromClaimDetails: ProviderInfoFromClaimDetails[]) {
  const providers: ProviderInfo[] = [];
  // Check for emergency service provider
  const emergencyServiceProviderInfo = providerInfoFromClaimDetails.filter((provider) =>
    _get(provider, 'serviceType', '').toLowerCase().includes(ServiceTypeCode.EMERGENCY_SERVICE_TYPE_CODE.toLowerCase())
  );
  if (emergencyServiceProviderInfo.length > 0) {
    const selectedProvider = emergencyServiceProviderInfo[0];
    const providerId = getProviderIDfromDisplayName(_get(selectedProvider, 'specialist.displayName', ''));
    const provider: ProviderInfo = {
      serviceTypeName: StageNames.EMERGENCY_SERVICES,
      serviceAvailable: '',
      providerName: _get(selectedProvider, 'specialist.displayName', ''),
      providerPhone: _get(selectedProvider, 'specialist.providerPhone', ''),
      providerID: providerId,
      providerUrl: getWebUrl(providerId),
      subServiceTypes: undefined,
      serviceCreationStatus: selectedProvider.status?.toUpperCase() === 'SUCCESS' ? true : false,
    };
    providers.push(provider);
  }
  // Check for water repair service provider
  const waterRemovalServiceProviderInfo = providerInfoFromClaimDetails.filter(
    (provider) =>
      _get(provider, 'serviceType', '').toLowerCase() === ServiceTypeCode.WATER_MITIGATION_TYPE_CODE.toLowerCase()
  );
  if (waterRemovalServiceProviderInfo.length > 0) {
    const selectedProvider = waterRemovalServiceProviderInfo[0];
    const providerId = getProviderIDfromDisplayName(_get(selectedProvider, 'specialist.displayName', ''));
    const provider: ProviderInfo = {
      serviceTypeName: StageNames.WATER_REMOVAL,
      serviceAvailable: '',
      providerName: _get(selectedProvider, 'specialist.displayName', ''),
      providerPhone: _get(selectedProvider, 'specialist.providerPhone', ''),
      providerID: providerId,
      providerUrl: getWebUrl(providerId),
      subServiceTypes: undefined,
      serviceCreationStatus: selectedProvider.status?.toUpperCase() === 'SUCCESS' ? true : false,
    };
    providers.push(provider);
  }
  // Check for document damage service provider
  const documentDamageServiceProviderInfo = providerInfoFromClaimDetails.filter(
    (provider) =>
      _get(provider, 'serviceType', '').toLowerCase() === ServiceTypeCode.DOCUMENT_DAMAGE_TYPE_CODE.toLowerCase()
  );
  if (documentDamageServiceProviderInfo.length > 0) {
    const provider: ProviderInfo = {
      serviceTypeName: StageNames.DOCUMENT_DAMAGE,
      serviceAvailable: '',
      providerName: '',
      providerID: '',
      providerPhone: '',
      providerUrl: '',
      subServiceTypes: undefined,
    };
    providers.push(provider);
  }
  // Check for repair service provider
  const repairServiceProviderInfo = providerInfoFromClaimDetails.filter(
    (provider) => _get(provider, 'serviceType', '').toLowerCase() === ServiceTypeCode.REPAIR_TYPE_CODE.toLowerCase()
  );
  if (repairServiceProviderInfo.length > 0) {
    const selectedProvider = repairServiceProviderInfo[0];
    const provider: ProviderInfo = {
      serviceTypeName: StageNames.REPAIR,
      serviceAvailable: '',
      providerAddress: getAutoShopAddress(selectedProvider.specialist),
      providerName: selectedProvider.specialist.displayName,
      providerID: '',
      providerPhone: '',
      providerUrl: '',
      subServiceTypes: undefined,
      serviceRequestDate:
        _get(selectedProvider, 'requestedDate', '') ||
        _get(selectedProvider, 'serviceRequestedOn', '') ||
        _get(selectedProvider, 'createDate', ''),
    };
    providers.push(provider);
  }

  // Check for rental service provider
  const rentalServiceProviderInfo = providerInfoFromClaimDetails.filter(
    (provider) => _get(provider, 'serviceType', '').toLowerCase() === ServiceTypeCode.RENTAL_TYPE_CODE.toLowerCase()
  );
  if (rentalServiceProviderInfo.length > 0) {
    const selectedProvider = rentalServiceProviderInfo[0];
    const provider: ProviderInfo = {
      serviceTypeName: StageNames.RENTAL,
      serviceAvailable: '',
      providerAddress: getAutoShopAddress(selectedProvider.specialist),
      providerName: selectedProvider.specialist.displayName,
      providerID: '',
      providerPhone: '',
      providerUrl: '',
      subServiceTypes: undefined,
      serviceRequestDate:
        _get(selectedProvider, 'requestedDate', '') ||
        _get(selectedProvider, 'serviceRequestedOn', '') ||
        _get(selectedProvider, 'createDate', ''),
    };
    providers.push(provider);
  }

  // Check for towing service provider
  const towingServiceProviderInfo = providerInfoFromClaimDetails.filter(
    (provider) => _get(provider, 'serviceType', '').toLowerCase() === ServiceTypeCode.TOWING_TYPE_CODE.toLowerCase()
  );
  if (towingServiceProviderInfo.length > 0) {
    const selectedProvider = towingServiceProviderInfo[0];
    const provider: ProviderInfo = {
      serviceTypeName: StageNames.TOWING,
      serviceAvailable: '',
      providerName: _get(selectedProvider, 'specialist.displayName', ''),
      providerPhone: '',
      serviceRequestDate:
        _get(selectedProvider, 'requestedDate', '') ||
        _get(selectedProvider, 'serviceRequestedOn', '') ||
        _get(selectedProvider, 'createDate', ''),
    };
    providers.push(provider);
  }
  return providers;
}

export const { updateClaimDetailState, updateClaimPhotosState, clearClaimPhotosState, updateClaimCvpRefNumState } =
  claimDetailSlice.actions;
export default claimDetailSlice.reducer;
